import React, { useState, useMemo } from 'react'
import {
  Input,
  Button,
  List,
  Tag,
  Card,
  Row,
  Col,
  Typography,
  Empty,
  Switch,
  Alert,
} from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import styled from 'styled-components/macro'
import { useAuth0 } from '../../auth0-wrapper'
import { getProductsBySKU } from './getProductsBySKU'
import { useStores } from '../../components/StoresProvider'
import { warehousesForApparel } from '../../constants'

const { Title, Text } = Typography

const SearchStores = () => {
  const [sku, setSku] = useState('')
  const [searchedSku, setSearchedSku] = useState('') // Store the SKU that was actually searched
  const [loading, setLoading] = useState(false)
  const [searchResults, setSearchResults] = useState([])
  const [hasSearched, setHasSearched] = useState(false)
  const { getTokenSilently } = useAuth0()
  const { stores } = useStores()

  // Check if stores are loaded
  const storesLoaded = stores && stores.length > 0

  const handleSearch = async () => {
    // Don't search if stores haven't loaded yet
    if (!storesLoaded) {
      console.warn('Stores not loaded yet, please wait...')
      return
    }

    setLoading(true)
    setHasSearched(false)
    setSearchResults([]) // Clear previous results when starting new search
    setSearchedSku(sku) // Store the SKU that is being searched

    try {
      const token = await getTokenSilently()

      const products = await getProductsBySKU(sku, token, stores)
      setSearchResults(products || [])
    } catch (error) {
      console.error('Error searching for SKU:', error)
      setSearchResults([])
    } finally {
      setLoading(false)
      setHasSearched(true)
    }
  }

  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  // Process search results to aggregate apparel warehouse quantities into FIEGE00
  const processedSearchResults = useMemo(() => {
    if (!searchResults || searchResults.length === 0) return []

    const apparelWarehouseIds = Object.keys(warehousesForApparel)

    // Find FIEGE00 item (id === "2")
    const fiege00Item = searchResults.find(
      item => item.inventorylocation?.id === '2',
    )

    if (!fiege00Item) {
      // No FIEGE00 item found, return results as-is but filter out apparel warehouses
      return searchResults.filter(
        item => !apparelWarehouseIds.includes(item.inventorylocation?.id),
      )
    }

    // Calculate total quantity on order from all apparel warehouses
    const totalApparelQuantityOnOrder = searchResults
      .filter(item => apparelWarehouseIds.includes(item.inventorylocation?.id))
      .reduce((sum, item) => sum + Number(item.locationquantityonorder || 0), 0)

    // Create updated FIEGE00 item with aggregated quantity on order
    const updatedFiege00Item = {
      ...fiege00Item,
      locationquantityonorder:
        Number(fiege00Item.locationquantityonorder || 0) +
        totalApparelQuantityOnOrder,
    }

    // Return all items except apparel warehouses, with updated FIEGE00 item
    return searchResults
      .filter(item => !apparelWarehouseIds.includes(item.inventorylocation?.id))
      .map(item =>
        item.inventorylocation?.id === '2' ? updatedFiege00Item : item,
      )
  }, [searchResults])

  const totalStock = useMemo(() => {
    return processedSearchResults.reduce(
      (sum, item) => sum + Number(item.locationquantityavailable),
      0,
    )
  }, [processedSearchResults])

  const amountOfProductsOnOrder = useMemo(() => {
    return processedSearchResults.reduce(
      (sum, item) => sum + Number(item.locationquantityonorder),
      0,
    )
  }, [processedSearchResults])

  const [hideNegativeResults, setHideNegativeResults] = useState(true)

  const handleHideNegativeResults = checked => {
    setHideNegativeResults(checked)
  }

  // Calculate first product display name from search results
  const firstProductDisplayName = useMemo(() => {
    if (searchResults.length > 0) {
      const firstProduct = searchResults[0]
      return `${firstProduct.displayname} - ${
        firstProduct.custitem1?.value
          ? firstProduct.custitem1?.value
          : '(Single size)'
      }`
    }
    return ''
  }, [searchResults])

  return (
    <Container>
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <Card>
            <Title level={2}>Cerca Negozio per SKU</Title>
            <Text type="secondary">
              Inserisci uno SKU per trovare tutti i negozi che hanno questo
              prodotto in magazzino.
            </Text>
            {!storesLoaded && (
              <Text
                type="warning"
                style={{ display: 'block', marginTop: '8px', fontSize: '12px' }}
              >
                Caricamento dati negozi... ({stores?.length || 0} negozi
                caricati)
              </Text>
            )}
            {storesLoaded && (
              <Text
                type="success"
                style={{ display: 'block', marginTop: '8px', fontSize: '12px' }}
              >
                ✓ {stores.length} negozi caricati e pronti
              </Text>
            )}

            <SearchSection>
              <SearchInput
                size="large"
                placeholder={
                  storesLoaded
                    ? 'Inserisci SKU (es. 0146380)'
                    : 'Caricamento negozi...'
                }
                value={sku}
                onChange={e => {
                  // we set the SKU to uppercase as soon as the user types it, because every character of the SKUs are uppercased.
                  setSku(e.target?.value?.toUpperCase())
                }}
                onKeyPress={handleKeyPress}
                prefix={<SearchOutlined />}
                disabled={loading || !storesLoaded}
              />
              <SearchButton
                type="primary"
                size="large"
                onClick={handleSearch}
                loading={loading}
                disabled={!sku.trim() || !storesLoaded}
                icon={<SearchOutlined />}
              >
                {storesLoaded ? 'Cerca' : 'Caricamento...'}
              </SearchButton>
            </SearchSection>
          </Card>
        </Col>

        {hasSearched && (
          <Col span={24}>
            <Card>
              {processedSearchResults.length > 0 ? (
                <>
                  <ResultsHeader>
                    <Title level={3}>
                      Risultati per SKU: "{searchedSku}" -{' '}
                      {firstProductDisplayName}
                    </Title>
                    <StatsRow>
                      <StatCard>
                        <StatNumber>{processedSearchResults.length}</StatNumber>
                        <StatLabel>Negozi</StatLabel>
                      </StatCard>
                      <StatCard>
                        <StatNumber>{totalStock}</StatNumber>
                        <StatLabel>Stock Totale</StatLabel>
                      </StatCard>
                    </StatsRow>
                  </ResultsHeader>
                  <SwitchContainer>
                    <span>Nascondi risultati negativi: </span>
                    <Switch
                      checked={hideNegativeResults}
                      onChange={handleHideNegativeResults}
                    />
                  </SwitchContainer>
                  {hideNegativeResults &&
                    totalStock === 0 &&
                    amountOfProductsOnOrder === 0 && (
                      <Alert
                        message="Presenti solo risultati negativi"
                        type="error"
                        showIcon
                      />
                    )}
                  <List
                    dataSource={processedSearchResults}
                    bordered
                    renderItem={item => {
                      if (
                        hideNegativeResults &&
                        Number(item.locationquantityavailable) <= 0 &&
                        Number(item.locationquantityonorder) <= 0
                      ) {
                        return null
                      }

                      return (
                        <List.Item>
                          <StoreInfo>
                            <LocationInfo>
                              Posizione: {item.inventorylocation?.value}
                            </LocationInfo>
                          </StoreInfo>
                          <StockInfo>
                            <Tag
                              color={
                                Number(item.locationquantityavailable) > 0
                                  ? 'green'
                                  : 'red'
                              }
                              style={{ fontSize: '14px', padding: '4px 8px' }}
                            >
                              Stock: {item.locationquantityavailable || 0}
                            </Tag>
                          </StockInfo>
                          <StockInfo>
                            <Tag
                              color={
                                Number(item.locationquantityonorder) > 0
                                  ? 'green'
                                  : 'red'
                              }
                              style={{ fontSize: '14px', padding: '4px 8px' }}
                            >
                              Già ordinati: {item.locationquantityonorder || 0}
                            </Tag>
                          </StockInfo>
                        </List.Item>
                      )
                    }}
                  />
                </>
              ) : (
                <Empty
                  description={
                    <div>
                      <p>
                        Nessuna disponibilità trovata per SKU "{searchedSku}"
                      </p>
                      <p style={{ fontSize: '12px', color: '#8c8c8c' }}>
                        Questo SKU potrebbe non esistere nel sistema.
                      </p>
                    </div>
                  }
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
            </Card>
          </Col>
        )}
      </Row>
    </Container>
  )
}

const Container = styled.div`
  width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
`

const SearchSection = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  align-items: stretch;
`

const SearchInput = styled(Input)`
  flex: 1;
`

const SearchButton = styled(Button)`
  min-width: 120px;
`

const ResultsHeader = styled.div`
  margin-bottom: 24px;
`

const StatsRow = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 16px;
`

const StatCard = styled.div`
  text-align: center;
  padding: 16px;
  background: #f5f5f5;
  border-radius: 8px;
  min-width: 100px;
`

const StatNumber = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #1890ff;
`

const StatLabel = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 4px;
`

const StoreInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const LocationInfo = styled.span`
  font-size: 16px;
  color: #595959;
  font-weight: bold;
`

const StockInfo = styled.div`
  display: flex;
  align-items: center;
`

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
  font-size: 16px;
`

export default SearchStores
